import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'universal-cookie';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDog } from '@fortawesome/pro-solid-svg-icons/faDog';
import { faCat } from '@fortawesome/pro-solid-svg-icons/faCat';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';

import { AgencyErrorModal } from 'us-react-components/dist/AgencyErrorModal/AgencyErrorModal';
import { BookingSuccessModal } from 'us-react-components/dist/BookingSuccessModal/BookingSuccessModal';
import { FamilyPaywallModal } from 'us-react-components/dist/FamilyPaywallModal/FamilyPaywallModal';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { MultipleSitterModal } from 'us-react-components/dist/MultipleSitterModal/MultipleSitterModal';
import { NoRelationshipModal } from 'us-react-components/dist/NoRelationshipModal/NoRelationshipModal';
import { PhotoGuideModal } from 'us-react-components/dist/PhotoGuideModal/PhotoGuideModal';
import { SittersNeededModal } from 'us-react-components/dist/SittersNeededModal/SittersNeededModal';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  formatDateRange,
  getMonthDayString,
  getShortWeekdayDateString,
} from 'us-react-components/dist/utils/date';
import {
  AgencyService,
  Booking,
  bookingVerticalType,
  BookingService,
  Include,
  IncludeService,
  JobService,
  ParentService,
  Profile,
  SitterService,
  Sweetener,
  SweetenerService,
  UserPictureService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { Storage } from '../../constants';
import { useDependencies } from '../../dependencies';

export type WizardStep =
  | 'frequency'
  | 'selection'
  | 'selectDate'
  | 'confirmMultipleDates'
  | 'addDetails'
  | 'agencyDate';

interface Props {
  step: WizardStep;
}

interface OpenJob {
  id?: number;
  type?: string;
  availability?: string;
  booking?: { start?: string; end?: string; typeId?: number };
}

interface SitterLite {
  id?: number;
  firstName?: string;
  pictureUrl?: string;
  rate?: number;
  interviewTimes?: string;
  acceptsAnyTime?: boolean;
  relationship?: {
    booked?: boolean;
  };
}

const VERTICALS = [
  { id: bookingVerticalType.CHILD_CARE, code: 'childCare', titleKey: 'childCare', bodyKey: 'childCareBody' },
  { id: bookingVerticalType.SENIOR_CARE, code: 'seniorCare', titleKey: 'seniorCare', bodyKey: 'seniorCareBody' },
  { id: bookingVerticalType.PET_CARE, code: 'petCare', titleKey: 'petCare', bodyKey: 'petCareBody' },
  { id: bookingVerticalType.HOUSEHOLD_SERVICES, code: 'householdServices', titleKey: 'householdServices', bodyKey: 'householdServicesBody' },
] as const;

const getVerticalTypeKey = (typeId?: number): string =>
  VERTICALS.find((v) => v.id === typeId)?.code ?? 'fallback';


// Mirrors select-date.js:102-107 — backend expects "YYYY-MM-DD HH:mm:ss" in the
// viewer's local time, not ISO-8601. Shared by SelectDateStep (for redirects)
// and AddDetailsStep (for the final booking POST).
const pad = (n: number): string => String(n).padStart(2, '0');
const toApiDateTime = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// 30-min slots rendered as "h:mm AM/PM"; underlying value is 24-hour "HH:mm".
interface TimeSlot { value: string; label: string }
const TIME_SLOTS: TimeSlot[] = ((): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 30]) {
      const value = `${pad(h)}:${pad(m)}`;
      const period = h < 12 ? 'AM' : 'PM';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      slots.push({ value, label: `${h12}:${pad(m)} ${period}` });
    }
  }
  return slots;
})();

// Split "YYYY-MM-DDTHH:mm" (datetime-local state format) into date/time halves.
const splitLocalDateTime = (s: string): { date: string; time: string } => {
  if (!s) return { date: '', time: '' };
  const [date, rest] = s.split('T');
  const time = rest ? rest.slice(0, 5) : '';
  return { date: date ?? '', time };
};

const joinLocalDateTime = (date: string, time: string): string => {
  if (!date || !time) return '';
  return `${date}T${time}`;
};

// Frequency step: "just once" vs "recurring". Mirrors Angular booking-
// frequency.tpl.html: left column has the choice cards, right column inlines
// either the select-date or select-multiple-dates form once a choice is made.
// Selecting a card does not navigate — the date form is rendered embedded and
// does its own submit-and-navigate to /parent/bookings/add-details.
const FrequencyStep = ({
  searchParams,
  userId,
}: {
  searchParams: URLSearchParams;
  userId?: number;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const sitterIdParam = Number(searchParams.get('sitterId')) || undefined;
  const [sitter, setSitter] = useState<{
    id?: number;
    firstName?: string;
    relationship?: {
      bookingCount?: number;
      lastMessaged?: string;
      parentNote?: { id?: number };
      positiveReview?: boolean;
    };
  } | null>(null);
  const [noRelationshipOpen, setNoRelationshipOpen] = useState(false);
  // undefined = no choice yet (show bg image + just the cards),
  // false = "Just once" picked (render SelectDateStep on the right),
  // true = "Recurring" picked (render ConfirmMultipleDatesStep on the right).
  const [isRecurring, setIsRecurring] = useState<boolean | undefined>(() => {
    if (searchParams.get('isRecurring') === 'true') return true;
    if (searchParams.get('instances')) return true;
    return undefined;
  });

  useEffect(() => {
    if (!sitterIdParam || !userId) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterIdParam, { params: { userId, parentId: userId } });
        setSitter((resp.data?.data ?? null) as typeof sitter);
      } catch {
        setSitter(null);
      }
    };
    void load();
  }, [sitterIdParam, userId]);

  const hasRelationship = (): boolean => {
    const rel = sitter?.relationship;
    if (!rel) return false;
    return !!(
      rel.bookingCount ||
      rel.lastMessaged ||
      rel.parentNote ||
      rel.positiveReview
    );
  };

  // Mirrors booking-frequency.js setRecurring(true): show the no-relationship
  // warning if the parent has never interacted with the sitter, otherwise
  // reveal the recurring date picker inline.
  const chooseRecurring = (): void => {
    if (sitter && !hasRelationship()) {
      setNoRelationshipOpen(true);
      return;
    }
    setIsRecurring(true);
  };

  const cardClass = (selected: boolean): string => [
    'text-left bg-white border-2 rounded-xl w-full px-4 py-5 block shadow-sm transition-all',
    selected
      ? 'border-sapphire shadow-md ring-1 ring-sapphire'
      : 'border-neutral-200 hover:border-sapphire hover:shadow-md',
  ].join(' ');

  return (
    <>
      {isRecurring === undefined && <div className='bkgd-img-kids-cabin-foyer' />}
      <div className='container py-8 relative'>
        <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
          <div className='md:col-span-4'>
            <h1 className='text-3xl font-semibold mb-5'>{t('pages.parentBookingWizard.frequency.heading')}</h1>
            <div className='space-y-3'>
              <Button
                id='create-one-booking'
                buttonType={ButtonType.tertiary}
                onClick={(): void => setIsRecurring(false)}
                className={cardClass(isRecurring === false)}
              >
                <h3 className='font-semibold mb-1 text-neutral-900'>{t('pages.parentBookingWizard.frequency.justOnce')}</h3>
                <div className='text-neutral-600'>
                  {t('pages.parentBookingWizard.frequency.justOnceBody')}
                </div>
              </Button>
              <Button
                id='create-recurring-booking'
                buttonType={ButtonType.tertiary}
                onClick={chooseRecurring}
                className={cardClass(isRecurring === true)}
              >
                <h3 className='font-semibold mb-1 text-neutral-900'>{t('pages.parentBookingWizard.frequency.recurring')}</h3>
                <div className='text-neutral-600'>
                  {t('pages.parentBookingWizard.frequency.recurringBody')}
                </div>
              </Button>
            </div>
          </div>
          <div className='md:col-span-8'>
            {isRecurring === false && (
              <SelectDateStep
                searchParams={searchParams}
                userId={userId}
                embedded
              />
            )}
            {isRecurring === true && (
              <ConfirmMultipleDatesStep searchParams={searchParams} embedded />
            )}
          </div>
        </div>
      </div>
      <NoRelationshipModal
        isOpen={noRelationshipOpen}
        onClose={(): void => setNoRelationshipOpen(false)}
        sitterFirstName={sitter?.firstName}
        onMessage={(): void => {
          setNoRelationshipOpen(false);
          if (userId && sitter?.id) {
            navigate(`/parent/${userId}/messages/${sitter.id}`);
          }
        }}
        onContinue={(): void => {
          setNoRelationshipOpen(false);
          setIsRecurring(true);
        }}
      />
    </>
  );
};

// Selection step: vertical type picker + optional use-from-open-job
const SelectionStep = ({
  searchParams,
  userId,
}: {
  searchParams: URLSearchParams;
  userId?: number;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const [openJobs, setOpenJobs] = useState<OpenJob[]>([]);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sitter, setSitter] = useState<SitterLite | null>(null);

  const sitterIdParam = Number(searchParams.get('sitterId')) || undefined;

  useEffect(() => {
    if (!userId) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await JobService.getByFilter({
          params: { parentId: userId, status: 'open', sort: 'updated', limit: 10 },
        });
        setOpenJobs((resp.data?.data ?? []) as OpenJob[]);
      } catch {
        setOpenJobs([]);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [userId]);

  useEffect(() => {
    if (!sitterIdParam || !userId) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterIdParam, { params: { userId, parentId: userId } });
        setSitter((resp.data?.data ?? null) as SitterLite | null);
      } catch {
        setSitter(null);
      }
    };
    void load();
  }, [sitterIdParam, userId]);

  // Mirrors booking-selection.js setType/selectJobPost: always hands off to
  // the frequency step, which picks just-once vs recurring and renders the
  // appropriate date picker inline.
  const setVertical = (code: string): void => {
    const sp = new URLSearchParams(searchParams);
    sp.set('bookingType', code);
    const vertical = VERTICALS.find((v) => v.code === code);
    if (vertical) sp.set('typeId', String(vertical.id));
    navigate(`/parent/bookings/frequency?${sp.toString()}`);
  };

  const selectJobPost = (job: OpenJob): void => {
    if (!job.id) return;
    const sp = new URLSearchParams(searchParams);
    sp.set('jobId', String(job.id));
    if (job.booking?.typeId) sp.set('typeId', String(job.booking.typeId));
    if (job.type === 'recurring') {
      sp.set('isRecurring', 'true');
    } else {
      // Pre-populate start/end from the job if the API returned them, then let
      // select-date confirm or refine the times before proceeding to add-details.
      if (job.booking?.start) sp.set('startDefault', job.booking.start);
      if (job.booking?.end) sp.set('endDefault', job.booking.end);
    }
    navigate(`/parent/bookings/frequency?${sp.toString()}`);
  };

  const skipToVertical = (): void => {
    setOpenJobs([]);
  };

  const SitterSummary = sitter ? (
    <div className='flex items-center gap-3 mb-5'>
      {sitter.pictureUrl && (
        <img
          src={sitter.pictureUrl}
          alt={sitter.firstName}
          className='w-14 h-14 rounded-full object-cover'
        />
      )}
      <div>
        <div className='font-semibold'>{sitter.firstName}</div>
        {sitter.rate != null && (
          <div className='text-sm text-neutral-600'>{t('pages.parentBookingWizard.selection.perHour', { rate: sitter.rate })}</div>
        )}
      </div>
    </div>
  ) : null;

  if (openJobs.length > 0 && !loading) {
    return (
      <>
        <div className='bkgd-img-kids-cabin-foyer' />
        <div className='container py-8 max-w-3xl relative'>
          {SitterSummary}
          <h1 className='text-3xl font-semibold mb-5'>
            {t('pages.parentBookingWizard.selection.usePostHeading')}
          </h1>
          <div className='mb-5'>
            <Button
              id='skip-to-vertical-selection'
              buttonType={ButtonType.primary}
              onClick={skipToVertical}
            >
              {t('pages.parentBookingWizard.selection.createNewBooking')}{' '}
              <i className='fa-solid fa-arrow-right ml-1' aria-hidden='true' />
            </Button>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4'>
            {openJobs.slice(0, showMore ? openJobs.length : 3).map((job) => (
              <Button
                key={job.id}
                id={`job-job-post-${job.id}`}
                buttonType={ButtonType.tertiary}
                onClick={(): void => selectJobPost(job)}
                className='text-left bg-white border-2 border-neutral-200 rounded-xl w-full px-4 py-4 block shadow-sm hover:border-sapphire hover:shadow-md transition-all'
              >
                <h3 className='font-bold mb-1 text-neutral-900'>
                  {t(`pages.parentBookingWizard.selection.careTypes.${getVerticalTypeKey(job.booking?.typeId)}`)}
                </h3>
                <div className='text-sm text-neutral-600'>
                  {job.type === 'recurring'
                    ? t('pages.parentBookingWizard.selection.recurringWithStart', {
                        availability: job.availability ?? '',
                        date: getShortWeekdayDateString(job.booking?.start),
                      })
                    : getShortWeekdayDateString(job.booking?.start)}
                </div>
              </Button>
            ))}
          </div>
          {openJobs.length > 3 && !showMore && (
            <Button
              id='view-more-posts'
              buttonType={ButtonType.tertiary}
              onClick={(): void => setShowMore(true)}
              className='btn btn-tertiary'
            >
              {t('pages.parentBookingWizard.selection.viewMore')}{' '}
              <i className='fa-solid fa-angle-down ml-1' />
            </Button>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className='bkgd-img-kids-cabin-foyer' />
      <div className='container py-8 max-w-3xl relative'>
        {SitterSummary}
        <h1 className='text-3xl font-semibold mb-5'>{t('pages.parentBookingWizard.selection.heading')}</h1>
        <div className='space-y-3 max-w-md'>
          {VERTICALS.map((v) => (
            <Button
              key={v.code}
              id={`create-${v.code.toLowerCase()}-booking`}
              buttonType={ButtonType.tertiary}
              onClick={(): void => setVertical(v.code)}
              className='text-left bg-white border-2 border-neutral-200 rounded-xl w-full px-4 py-5 block shadow-sm hover:border-sapphire hover:shadow-md transition-all'
            >
              <h3 className='font-semibold mb-1 text-neutral-900'>
                {t(`pages.parentBookingWizard.selection.careTypes.${v.titleKey}`)}
              </h3>
              <div className='text-neutral-600'>
                {t(`pages.parentBookingWizard.selection.careTypes.${v.bodyKey}`)}
              </div>
            </Button>
          ))}
        </div>
      </div>
    </>
  );
};

// selectDate step: datetime picker + sitter summary + Continue.
// `embedded` is set when this is rendered inline by the FrequencyStep — in that
// mode we drop the outer container/h1 since the parent already provides them.
const SelectDateStep = ({
  searchParams,
  userId,
  isAgency,
  embedded,
}: {
  searchParams: URLSearchParams;
  userId?: number;
  isAgency?: boolean;
  embedded?: boolean;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const [sitter, setSitter] = useState<SitterLite | null>(null);

  // Pre-populate start/end from URL params when arriving from a job post selection.
  // State stays in "YYYY-MM-DDTHH:mm" (local time) so it can splice back into
  // the date/time pair controls and be parsed deterministically by `new Date()`.
  const toDatetimeLocal = (iso: string | null): string => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
  };

  // Agency flow defaults — today 4pm–10pm, rolling to tomorrow if already past 4pm.
  // Mirrors Angular agency-date.js#setDefaultStartTime + us-booking.resource#initParamsAndReturnDates.
  const agencyDefaults = ((): { start: string; end: string } | null => {
    if (!isAgency) return null;
    const now = new Date();
    const fourPM = new Date();
    fourPM.setHours(16, 0, 0, 0);
    const target = new Date();
    target.setHours(0, 0, 0, 0);
    if (now >= fourPM) target.setDate(target.getDate() + 1);
    const dateStr = `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`;
    return { start: `${dateStr}T16:00`, end: `${dateStr}T22:00` };
  })();

  const [start, setStart] = useState(
    () => toDatetimeLocal(searchParams.get('startDefault')) || agencyDefaults?.start || '',
  );
  const [end, setEnd] = useState(
    () => toDatetimeLocal(searchParams.get('endDefault')) || agencyDefaults?.end || '',
  );
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const sitterId = Number(searchParams.get('sitterId')) || undefined;

  useEffect(() => {
    if (!sitterId || !userId) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterId, { params: { userId, parentId: userId } });
        setSitter((resp.data?.data ?? null) as SitterLite | null);
      } catch {
        setSitter(null);
      }
    };
    void load();
  }, [sitterId, userId]);

  const [groupBookings, setGroupBookings] = useState<
    Array<Booking & { caregiver?: { pictureUrl?: string } }>
  >([]);
  const [group, setGroup] = useState<{ id?: number } | null>(null);
  const [multipleOpen, setMultipleOpen] = useState(false);
  const [sittersNeededOpen, setSittersNeededOpen] = useState(false);
  // Mirrors select-date.js `unavailable` + `isBooked`. When the sitter has no
  // availability in the requested window we surface one of two fallback UIs.
  const [unavailable, setUnavailable] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  const navigateToAddDetails = (extra?: Record<string, string>): void => {
    const sp = new URLSearchParams(searchParams);
    sp.set('start', new Date(start).toISOString());
    sp.set('end', new Date(end).toISOString());
    if (group?.id) sp.set('groupId', String(group.id));
    if (extra) {
      for (const [k, v] of Object.entries(extra)) sp.set(k, v);
    }
    navigate(`/parent/bookings/add-details?${sp.toString()}`);
  };

  // Walks through existing-group check then forwards to AddDetails. Called
  // both from the happy path and from "Send Request Anyway".
  const proceedToAddDetails = async (): Promise<void> => {
    if (userId && !isAgency) {
      try {
        const resp = await BookingService.getByFilter({
          params: {
            parentId: userId,
            userId,
            start: new Date(start).toISOString(),
          },
        });
        const data = (resp.data?.data ?? []) as Array<
          Booking & { group?: { id?: number }; caregiver?: { pictureUrl?: string } }
        >;
        if (data.length === 1 && data[0].group?.id) {
          setGroupBookings(data);
          setGroup({ id: data[0].group.id });
          setIsBooked(true);
          setMultipleOpen(true);
          return;
        }
      } catch {
        // Non-fatal: conflict check shouldn't block.
      }
    }
    navigateToAddDetails();
  };

  const continueBooking = async (): Promise<void> => {
    if (!start || !end) {
      setError('Please enter both start and end date/time.');
      return;
    }
    if (new Date(end).getTime() <= new Date(start).getTime()) {
      setError('End must be after start.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      // Mirrors select-date.js:95-137 checkAvailability(). Skip for agency
      // bookings (no sitterId context) and when no sitterId query param.
      if (sitterId && !isAgency) {
        try {
          const resp = await SitterService.getSchedules({
            params: {
              sitterId,
              start: toApiDateTime(start),
              end: toApiDateTime(end),
              available: true,
            },
          });
          const schedules = (resp.data?.data ?? []) as unknown[];
          // Empty list from the /v2/sitter-schedules endpoint (with
          // `available=true`) means the sitter has no availability window
          // overlapping [start, end). Angular treats an error response the
          // same way — both paths set `unavailable=true`.
          if (schedules.length === 0) {
            setUnavailable(true);
            return;
          }
        } catch {
          setUnavailable(true);
          return;
        }
      }
      setUnavailable(false);
      await proceedToAddDetails();
    } finally {
      setSubmitting(false);
    }
  };

  const sendRequestAnyway = async (): Promise<void> => {
    setSubmitting(true);
    try {
      setUnavailable(false);
      await proceedToAddDetails();
    } finally {
      setSubmitting(false);
    }
  };

  const goToSitterSearch = (): void => {
    const sp = new URLSearchParams();
    if (start) sp.set('start', new Date(start).toISOString());
    if (end) sp.set('end', new Date(end).toISOString());
    navigate(sp.toString() ? `/sitters?${sp}` : '/sitters');
  };

  const Wrapper = ({ children }: { children: ReactNode }): JSX.Element =>
    embedded ? (
      <div>{children}</div>
    ) : (
      <div className='container py-8 max-w-2xl'>{children}</div>
    );

  return (
    <Wrapper>
      {!embedded && (
        <>
          <h1 className='text-3xl font-semibold mb-2'>
            {isAgency
              ? t('pages.parentBookingWizard.selectDate.agencyHeading')
              : t('pages.parentBookingWizard.selectDate.heading')}
          </h1>
          <p className='mb-5'>{t('pages.parentBookingWizard.selectDate.subheading')}</p>
        </>
      )}

      {sitter && (
        <div className='flex items-center gap-3 mb-5'>
          {sitter.pictureUrl && (
            <img
              src={sitter.pictureUrl}
              alt={sitter.firstName}
              className='w-14 h-14 rounded-full object-cover'
            />
          )}
          <div>
            <div className='font-semibold'>{sitter.firstName}</div>
            {sitter.rate != null && (
              <div className='text-sm text-neutral-600'>{t('pages.parentBookingWizard.selection.perHour', { rate: sitter.rate })}</div>
            )}
            {sitter.interviewTimes && (
              <div className='text-xs text-neutral-600'>
                {t('pages.parentBookingWizard.selectDate.interviewTimes', { times: sitter.interviewTimes })}
              </div>
            )}
          </div>
        </div>
      )}

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4'>
        {(['start', 'end'] as const).map((key) => {
          const value = key === 'start' ? start : end;
          const set = key === 'start' ? setStart : setEnd;
          const { date, time } = splitLocalDateTime(value);
          return (
            <div key={key}>
              <label htmlFor={`${key}-date`} className='block font-semibold mb-1'>
                {key === 'start' ? 'Start' : 'End'}
              </label>
              <div className='flex gap-2'>
                <input
                  id={`${key}-date`}
                  type='date'
                  className='form-control flex-1 border border-neutral-300 rounded px-3 py-2'
                  value={date}
                  onChange={(e): void => {
                    set(joinLocalDateTime(e.target.value, time || '18:00'));
                    setUnavailable(false);
                  }}
                />
                <select
                  id={`${key}-time`}
                  className='form-control border border-neutral-300 rounded px-3 py-2'
                  value={time}
                  onChange={(e): void => {
                    set(joinLocalDateTime(date, e.target.value));
                    setUnavailable(false);
                  }}
                >
                  <option value=''>{t('pages.parentBookingWizard.selectDate.selectTime')}</option>
                  {TIME_SLOTS.map((slot) => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>

      {error && <p className='text-red-600 text-sm mb-3'>{error}</p>}

      {start && end && !Number.isNaN(new Date(start).getTime()) && !Number.isNaN(new Date(end).getTime()) && (new Date(end).getTime() - new Date(start).getTime()) / 3_600_000 > 24 && (
        // Mirrors select-date.js:63-69 FYI banner.
        <div className='mb-4 p-3 rounded border border-sapphire-200 bg-sapphire-50'>
          <div className='font-semibold mb-1'>FYI</div>
          <div className='text-sm'>
            One-time bookings exceeding 24 hours are not eligible for flat rate
            payments. Consider scheduling a recurring booking instead.
          </div>
        </div>
      )}

      {unavailable && (
        <div className='mb-4 p-3 rounded border border-honey-300 bg-honey-50'>
          <p className='mb-3'>
            {sitter?.firstName
              ? t('pages.parentBookingWizard.selectDate.unavailable', { name: sitter.firstName })
              : t('pages.parentBookingWizard.selectDate.unavailableFallback')}
          </p>
          {sitter?.acceptsAnyTime && !isBooked ? (
            <div className='flex flex-wrap gap-2'>
              <Button
                buttonType={ButtonType.secondary}
                onClick={(): void => setUnavailable(false)}
              >
                {t('pages.parentBookingWizard.selectDate.changeDate')}
              </Button>
              <Button
                id='send-request-anyway'
                buttonType={ButtonType.primary}
                onClick={sendRequestAnyway}
                disabled={submitting}
              >
                {t('pages.parentBookingWizard.selectDate.sendRequestAnyway')}
              </Button>
            </div>
          ) : (
            <Button
              id='see-available-caregivers'
              buttonType={ButtonType.primary}
              onClick={goToSitterSearch}
            >
              {t('pages.parentBookingWizard.selectDate.seeAvailableCaregivers')}
            </Button>
          )}
        </div>
      )}

      {sitter?.id && (
        <a
          id='view-calendar'
          href={`/app/sitter/${sitter.id}/calendar`}
          target='_blank'
          rel='noreferrer'
          className='inline-block mb-5 underline'
        >
          <i className='fa-regular fa-calendar mr-1' />
          {t('pages.parentBookingWizard.selectDate.viewAvailability', { name: sitter.firstName ?? '' })}
        </a>
      )}

      <div className='flex gap-3'>
        <Button
          id='btn-continue'
          buttonType={ButtonType.primary}
          onClick={continueBooking}
          disabled={submitting}
        >
          {t('common.continue')}
        </Button>
        <Button
          buttonType={ButtonType.secondary}
          onClick={(): void => navigate(-1)}
        >
          {t('common.back')}
        </Button>
      </div>

      <MultipleSitterModal
        isOpen={multipleOpen}
        onClose={(): void => setMultipleOpen(false)}
        groupBookings={groupBookings}
        sitter={sitter ?? undefined}
        onFirstAccepter={(): void => {
          setMultipleOpen(false);
          navigateToAddDetails();
        }}
        onPickCount={(): void => {
          setMultipleOpen(false);
          setSittersNeededOpen(true);
        }}
      />
      {group && (
        <SittersNeededModal
          isOpen={sittersNeededOpen}
          onClose={(): void => setSittersNeededOpen(false)}
          group={group}
          groupBookings={groupBookings}
          sitter={sitter ?? undefined}
          onContinue={(): void => {
            setSittersNeededOpen(false);
            navigateToAddDetails();
          }}
        />
      )}
    </Wrapper>
  );
};

// Encode/decode the recurring `instances` array as a URL-safe base64 JSON blob
// passed via the `instances` search param between ConfirmMultipleDatesStep and
// AddDetailsStep. Angular kept this in BookingRecurringTimes service state;
// we use the URL so that refresh / back behavior is deterministic.
interface RecurringInstance {
  start: string;
  end: string;
}

const encodeInstances = (instances: RecurringInstance[]): string => {
  const json = JSON.stringify(instances);
  // btoa is available in all browsers; we use Unicode-safe encoding just in
  // case of future fields.
  return btoa(unescape(encodeURIComponent(json)));
};

const decodeInstances = (raw: string | null): RecurringInstance[] => {
  if (!raw) return [];
  try {
    const json = decodeURIComponent(escape(atob(raw)));
    const parsed = JSON.parse(json) as RecurringInstance[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p) => p?.start && p?.end);
  } catch {
    return [];
  }
};

// confirmMultipleDates step: list dates + confirm.
// `embedded` is set when this is rendered inline by the FrequencyStep — in that
// mode we drop the outer container/h1 since the parent already provides them.
const ConfirmMultipleDatesStep = ({
  searchParams,
  embedded,
}: {
  searchParams: URLSearchParams;
  embedded?: boolean;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const [dates, setDates] = useState<Array<{ start: string; end: string }>>(() => {
    // Support legacy dateStart/dateEnd params as well as the encoded form.
    const existing = decodeInstances(searchParams.get('instances'));
    if (existing.length) return existing;
    const starts = searchParams.getAll('dateStart');
    const ends = searchParams.getAll('dateEnd');
    return starts.map((s, i) => ({ start: s, end: ends[i] ?? '' }));
  });

  const addDate = (): void => {
    setDates((prev) => [...prev, { start: '', end: '' }]);
  };
  const removeDate = (i: number): void => {
    setDates((prev) => prev.filter((_, idx) => idx !== i));
  };
  const updateDate = (i: number, key: 'start' | 'end', val: string): void => {
    setDates((prev) => prev.map((d, idx) => (idx === i ? { ...d, [key]: val } : d)));
  };

  const continueBooking = (): void => {
    const valid = dates.filter((d) => d.start && d.end);
    if (valid.length === 0) {
      toast.error(t('pages.parentBookingWizard.confirmDates.addAtLeastOne'));
      return;
    }
    const sp = new URLSearchParams(searchParams);
    sp.delete('dateStart');
    sp.delete('dateEnd');
    const instances: RecurringInstance[] = valid.map((d) => ({
      start: new Date(d.start).toISOString(),
      end: new Date(d.end).toISOString(),
    }));
    // Thread instances AND set top-level start/end so the AddDetails step's
    // existing validation (requires start + end) keeps working.
    sp.set('instances', encodeInstances(instances));
    sp.set('start', instances[0].start);
    sp.set('end', instances[0].end);
    navigate(`/parent/bookings/add-details?${sp.toString()}`);
  };

  const Wrapper = ({ children }: { children: ReactNode }): JSX.Element =>
    embedded ? (
      <div>{children}</div>
    ) : (
      <div className='container py-8 max-w-3xl'>{children}</div>
    );

  return (
    <Wrapper>
      {!embedded && (
        <>
          <h1 className='text-3xl font-semibold mb-3'>{t('pages.parentBookingWizard.confirmDates.heading')}</h1>
          <p className='mb-4'>{t('pages.parentBookingWizard.confirmDates.body')}</p>
        </>
      )}

      {dates.length === 0 && (
        <p className='text-neutral-500 mb-4'>{t('pages.parentBookingWizard.confirmDates.empty')}</p>
      )}

      <div className='space-y-3 mb-4'>
        {dates.map((d, i) => (
          <div key={i} className='grid grid-cols-1 sm:grid-cols-12 gap-3 items-end'>
            <div className='sm:col-span-5'>
              <label className='block font-semibold mb-1 text-sm'>{t('pages.parentBookingWizard.confirmDates.start')}</label>
              <input
                type='datetime-local'
                className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                value={d.start}
                onChange={(e): void => updateDate(i, 'start', e.target.value)}
              />
            </div>
            <div className='sm:col-span-5'>
              <label className='block font-semibold mb-1 text-sm'>{t('pages.parentBookingWizard.confirmDates.end')}</label>
              <input
                type='datetime-local'
                className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                value={d.end}
                onChange={(e): void => updateDate(i, 'end', e.target.value)}
              />
            </div>
            <div className='sm:col-span-2'>
              <Button
                buttonType={ButtonType.tertiary}
                onClick={(): void => removeDate(i)}
                className='btn btn-tertiary w-full'
              >
                {t('common.remove')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Button
        buttonType={ButtonType.tertiary}
        onClick={addDate}
        className='underline text-sapphire mb-5'
      >
        {t('pages.parentBookingWizard.confirmDates.addAnother')}
      </Button>

      <div className='flex gap-3'>
        <Button buttonType={ButtonType.primary} onClick={continueBooking}>
          {t('common.continue')}
        </Button>
        <Button
          buttonType={ButtonType.secondary}
          onClick={(): void => navigate(-1)}
        >
          {t('common.back')}
        </Button>
      </div>
    </Wrapper>
  );
};

// Agency caregiver type id (= 2) — drives the agency-booking branch.
const CAREGIVER_TYPE_AGENCY = 2;

// Local photo-upload widget. Kept local so ParentBookingWizard stays
// self-contained.
const ProfilePhotoUploader = ({
  parentId,
  pictureUrl,
  onUploaded,
}: {
  parentId?: number;
  pictureUrl?: string;
  onUploaded: () => void;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);

  const onChange = async (file: File): Promise<void> => {
    if (!parentId) return;
    if (file.size > 10 * 1024 * 1024) {
      setErr(t('pages.parentBookingWizard.photo.tooLarge'));
      return;
    }
    setUploading(true);
    setErr('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', String(parentId));
      await UserPictureService.create(formData as unknown as { userId?: number; file?: string });
      onUploaded();
    } catch {
      setErr(t('pages.parentBookingWizard.photo.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const hasPhoto = !!pictureUrl && pictureUrl.indexOf('us-default-avatar') === -1;

  return (
    <div className='flex flex-col items-center mb-4'>
      <label className='cursor-pointer'>
        <input
          type='file'
          accept='image/*'
          className='sr-only'
          onChange={(e): void => {
            const f = e.target.files?.[0];
            if (f) void onChange(f);
          }}
        />
        {hasPhoto ? (
          <img
            alt={t('pages.parentBookingWizard.photo.photoAlt')}
            src={pictureUrl}
            className='w-24 h-24 rounded-full object-cover border-2 border-neutral-300'
          />
        ) : (
          <div className='w-24 h-24 rounded-full border-2 border-dashed border-sapphire flex items-center justify-center text-center text-xs p-2 text-sapphire'>
            {t('pages.parentBookingWizard.photo.uploadPrompt')}
          </div>
        )}
      </label>
      {uploading && <p className='text-sm mt-1'>{t('pages.parentBookingWizard.photo.uploading')}</p>}
      {err && <p className='text-red-600 text-sm mt-1'>{err}</p>}
      <Button
        buttonType={ButtonType.tertiary}
        onClick={(): void => setGuideOpen(true)}
        className='mt-1 text-sm underline'
      >
        {t('pages.parentBookingWizard.photo.photoTips')}
      </Button>
      <PhotoGuideModal
        isOpen={guideOpen}
        onClose={(): void => setGuideOpen(false)}
        variant='parent'
      />
    </div>
  );
};

// addDetails step: kids/pets/seniors + description + includes/sweeteners + payment + address + phone
// Angular source: apps/angular/src/js/ng/parent/bookings/add-details/ and its 4 edit sub-components.
const DESC_MAX = 1000;
const STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY',
];

const isValidPhone = (p: string): boolean => /^\d{3}-?\d{3}-?\d{4}$/.test(p);

const AddDetailsStep = observer(({
  searchParams,
  userId,
}: {
  searchParams: URLSearchParams;
  userId?: number;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const { services: { tracker, config }, stores } = useDependencies();
  const parent = stores.parent.parent;
  const sitterIdParam = Number(searchParams.get('sitterId')) || undefined;
  const agencyIdParam = Number(searchParams.get('agencyId')) || undefined;
  const caregiverTypeParam = searchParams.get('caregiverType');
  const isAgencyBooking =
    caregiverTypeParam === 'agency' ||
    caregiverTypeParam === String(CAREGIVER_TYPE_AGENCY) ||
    !!agencyIdParam;
  const start = searchParams.get('start');
  const end = searchParams.get('end');
  const bookingIdParam = Number(searchParams.get('bookingId')) || undefined;
  const jobIdParam = Number(searchParams.get('jobId')) || undefined;
  const typeId = Number(searchParams.get('typeId')) || 1;
  const isChildCare = typeId === 1;
  const isPetCare = typeId === 2;
  const isSeniorCare = typeId === 3;
  const isHouseholdServices = typeId === 4;

  // Parse any recurring instances threaded through from the
  // ConfirmMultipleDates step.
  const instances = useMemo(
    () => decodeInstances(searchParams.get('instances')),
    [searchParams],
  );
  const isRecurring = instances.length > 0;

  // Mirrors add-details.js:168 `canSelectFlatRate` — flat_rate is only valid
  // for recurring bookings or one-time bookings <= 24 hours. Angular hides
  // the option + falls back to 'hourly' when false (booking-edit-payment.js:34).
  const canSelectFlatRate = useMemo(() => {
    if (isRecurring) return true;
    if (!start || !end) return true;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return true;
    return (e - s) / 3_600_000 <= 24;
  }, [isRecurring, start, end]);

  const [sitter, setSitter] = useState<{
    id?: number;
    firstName?: string;
    pictureUrl?: string;
    acceptsCreditCards?: boolean;
    rate?: number;
    rates?: Array<{ minimumRate?: number; maximumRate?: number }>;
    relationship?: { booked?: boolean };
  } | null>(null);
  const [agency, setAgency] = useState<Profile | null>(null);

  const [numberOfChildren, setNumberOfChildren] = useState(1);
  const [kidsAges, setKidsAges] = useState('');
  const [hasDog, setHasDog] = useState(false);
  const [hasCat, setHasCat] = useState(false);
  const [hasOtherPet, setHasOtherPet] = useState(false);

  const [description, setDescription] = useState('');
  const [includes, setIncludes] = useState<Array<Include & { selected?: boolean }>>([]);
  const [sweeteners, setSweeteners] = useState<Array<Sweetener & { selected?: boolean }>>([]);

  const [rate, setRate] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState<'hourly' | 'flat_rate'>('hourly');
  const [paymentType, setPaymentType] = useState<'credit' | 'cash'>('credit');

  // If the booking window shrinks/grows across the 24-hour threshold after the
  // user already chose flat_rate, revert to hourly — matches booking-edit-
  // payment.js:34.
  useEffect(() => {
    if (!canSelectFlatRate && paymentFrequency === 'flat_rate') {
      setPaymentFrequency('hourly');
    }
  }, [canSelectFlatRate, paymentFrequency]);

  const [street, setStreet] = useState('');
  const [street2, setStreet2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [neighborhood, setNeighborhood] = useState('');

  const [phone, setPhone] = useState('');
  const [hasStoredPhone, setHasStoredPhone] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const [agencyErrorOpen, setAgencyErrorOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallPaymentOnly, setPaywallPaymentOnly] = useState(false);
  const [lastBooking, setLastBooking] = useState<Booking | null>(null);
  const cookies = new Cookies();

  useEffect(() => {
    if (!userId) return;
    if (isAgencyBooking) {
      // Agency branch: no individual sitter. Angular loads the agency and
      // stores it on booking.caregiver (add-details.js:97).
      if (!agencyIdParam) return;
      void (async (): Promise<void> => {
        try {
          const resp = await AgencyService.getById(agencyIdParam, { params: { userId } });
          setAgency((resp.data?.data ?? null) as Profile | null);
        } catch (e) {
          console.warn('[AddDetails] agency load', e);
        }
      })();
      // Agency path defaults payment to credit (add-details.js:510).
      setPaymentType('credit');
      return;
    }
    if (!sitterIdParam) return;
    void (async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterIdParam, {
          params: { userId, parentId: userId },
        });
        const s = resp.data?.data;
        if (s) {
          setSitter(s as typeof sitter);
          if (s.rates?.[0]?.minimumRate) {
            setRate(String(s.rates[0].minimumRate));
          }
          setPaymentType(s.acceptsCreditCards ? 'credit' : 'cash');
        }
      } catch (e) {
        console.warn('[AddDetails] sitter load', e);
      }
    })();
  }, [sitterIdParam, agencyIdParam, isAgencyBooking, userId]);

  useEffect(() => {
    if (!userId) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await ParentService.get(userId, { params: { userId } });
        const p = resp.data?.data;
        if (p?.phone) {
          setPhone(p.phone);
          setHasStoredPhone(true);
        }
        if (p?.address) {
          setStreet(p.address.street ?? '');
          setStreet2(p.address.street2 ?? '');
          setCity(p.address.city ?? '');
          setState(p.address.state ?? '');
          setZip(p.address.zip ?? '');
          setNeighborhood(p.address.neighborhood ?? '');
        }
        // Mirror Angular setChildrenDefaults(): pre-fill child count and ages
        // from parent profile when no booking-level data is present.
        if (isChildCare && p?.kids?.length) {
          setNumberOfChildren(p.kids.length);
          if (p.kidAges) setKidsAges(p.kidAges);
        }
      } catch (e) {
        console.warn('[AddDetails] parent load', e);
      }
    };
    void load();
  }, [userId, isChildCare]);

  // Mirror Angular setBookingDefaults(): when the user arrives via "Book Again"
  // (bookingId in URL), pre-populate form fields from that previous booking.
  useEffect(() => {
    if (!bookingIdParam || !userId) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await BookingService.get(bookingIdParam, { params: { userId } });
        const b = resp.data?.data as (Booking & {
          kidsAges?: string;
          hasDog?: boolean;
          hasCat?: boolean;
          hasOtherPet?: boolean;
          paymentFrequency?: { id?: number };
        }) | null;
        if (!b) return;
        if (b.address) {
          setStreet(b.address.street ?? '');
          setStreet2(b.address.street2 ?? '');
          setCity(b.address.city ?? '');
          setState(b.address.state ?? '');
          setZip(b.address.zip ?? '');
        }
        const bookingDescription = b.description || (b as { message?: string }).message;
        if (bookingDescription) setDescription(bookingDescription);
        if (b.numberOfChildren) setNumberOfChildren(b.numberOfChildren);
        if (b.kidsAges) setKidsAges(b.kidsAges);
        if (b.hasDog !== undefined) setHasDog(b.hasDog);
        if (b.hasCat !== undefined) setHasCat(b.hasCat);
        if (b.hasOtherPet !== undefined) setHasOtherPet(b.hasOtherPet);
        if (b.rate) setRate(String(b.rate));
        if (b.paymentType === 'credit' || b.paymentType === 'cash') setPaymentType(b.paymentType);
        if (b.paymentFrequency?.id === 2) setPaymentFrequency('flat_rate');
        else if (b.paymentFrequency?.id === 1) setPaymentFrequency('hourly');
      } catch (e) {
        console.warn('[AddDetails] bookingId pre-fill load', e);
      }
    };
    void load();
  }, [bookingIdParam, userId]);

  // Pre-fill from job post when user arrives via SelectionStep (mirrors Angular setBookingDefaults)
  useEffect(() => {
    if (!jobIdParam || !userId) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await JobService.findById(jobIdParam, { params: { userId } });
        const job = resp.data?.data as {
          booking?: {
            address?: { street?: string; street2?: string; city?: string; state?: string; zip?: string; neighborhood?: string };
            numberOfChildren?: number;
            kidsAges?: string;
            description?: string;
            rate?: number;
            paymentType?: string;
            includes?: Array<{ id?: number }>;
            sweeteners?: Array<{ id?: number }>;
          };
        } | null;
        const b = job?.booking;
        if (!b) return;
        if (b.address?.city) {
          if (b.address.street) setStreet(b.address.street);
          if (b.address.street2) setStreet2(b.address.street2);
          setCity(b.address.city);
          if (b.address.state) setState(b.address.state);
          if (b.address.zip) setZip(b.address.zip);
          if (b.address.neighborhood) setNeighborhood(b.address.neighborhood);
        }
        if (b.numberOfChildren) setNumberOfChildren(b.numberOfChildren);
        if (b.kidsAges) setKidsAges(b.kidsAges);
        const descriptionText = (b as { message?: string }).message || b.description;
        if (descriptionText) setDescription(descriptionText);
        if (b.rate && b.rate >= 8) setRate(String(b.rate));
      } catch (e) {
        console.warn('[AddDetails] jobId pre-fill load', e);
      }
    };
    void load();
  }, [jobIdParam, userId]);

  useEffect(() => {
    if (!isChildCare) return;
    const loadIncludes = async (): Promise<void> => {
      try {
        const resp = await IncludeService.getBookingIncludes();
        setIncludes(((resp.data?.data ?? []) as Include[]).map((i) => ({ ...i, selected: false })));
      } catch {
        setIncludes([]);
      }
    };
    const loadSweeteners = async (): Promise<void> => {
      try {
        const resp = await SweetenerService.getBookingSweeteners();
        setSweeteners(((resp.data?.data ?? []) as Sweetener[]).map((s) => ({ ...s, selected: false })));
      } catch {
        setSweeteners([]);
      }
    };
    void loadIncludes();
    void loadSweeteners();
  }, [isChildCare]);

  const dependentLabel = isChildCare
    ? t('pages.parentBookingWizard.addDetails.children')
    : isPetCare
      ? t('pages.parentBookingWizard.addDetails.pets')
      : isSeniorCare
        ? t('pages.parentBookingWizard.addDetails.receivingCare')
        : '';

  const hasSelectedPets = hasDog || hasCat || hasOtherPet;
  const isRateValid = !!rate && Number(rate) >= 8;
  const isAddressComplete =
    !!street && !!city && !!state && !!zip && zip.length === 5;
  const isPhoneValid = !hasStoredPhone ? isValidPhone(phone) : true;

  // Min-rate warning banner (Angular: add-details.tpl.html:2 icon-banner).
  // TODO: verify — Angular uses bookingRateService.getSitterMinimumRate which
  // indexes by numberOfChildren. Using first entry is sufficient for the
  // common case (single rate); verify vs. multi-rate sitters.
  const sitterMinimumRate = ((): number | undefined => {
    if (!sitter?.rates || sitter.rates.length === 0) return undefined;
    const idx = Math.min(numberOfChildren - 1, sitter.rates.length - 1);
    const entry = sitter.rates[idx < 0 ? 0 : idx];
    return entry?.minimumRate;
  })();
  const showMinRateWarning =
    !isAgencyBooking &&
    !!sitter &&
    !!sitterMinimumRate &&
    !!rate &&
    Number(rate) < sitterMinimumRate;

  const canSubmit = ((): boolean => {
    if (submitting) return false;
    if (!numberOfChildren) return false;
    if (isChildCare && !kidsAges) return false;
    if (isPetCare && !hasSelectedPets) return false;
    if (!isRateValid) return false;
    if (!isAddressComplete) return false;
    if (!isPhoneValid) return false;
    return true;
  })();

  const submit = async (): Promise<void> => {
    if (!userId) {
      setError('You must be logged in to book. Please refresh and try again.');
      return;
    }
    if (!isRecurring && (!start || !end)) {
      setError('Date and time are missing. Click "Change date or time" to set them.');
      return;
    }
    if (isRecurring && instances.length === 0) {
      setError('No recurring dates were chosen. Click "Change date or time" to add some.');
      return;
    }
    // Sitter is only required for non-agency bookings.
    if (!isAgencyBooking && !sitterIdParam) {
      setError('No caregiver selected. Please return to the caregiver search and pick someone.');
      return;
    }
    if (isAgencyBooking && !agencyIdParam) {
      // Mirrors add-details.js:564 — open agency error modal if agencyId
      // wasn't selected yet.
      setAgencyErrorOpen(true);
      return;
    }
    setSubmitting(true);
    setError('');
    // Angular add-details.js:558 — fires before submit for first-time bookings
    // (non-agency, no prior booking with this sitter).
    const isFirstBooking = !isAgencyBooking && !sitter?.relationship?.booked;
    if (isFirstBooking) {
      tracker.track('booking-details', {
        action: 'first-booking-sent',
        label: 'Send Request Button',
      });
    }
    try {
      if (!hasStoredPhone && phone) {
        await ParentService.patch(userId, { id: userId, phone } as never);
      }
      const selectedIncludes = includes.filter((i) => i.selected);
      const selectedSweeteners = sweeteners.filter((s) => s.selected);
      const basePayload = {
        userId,
        // BE's BookingParentUpdater reads booking.parent.id (not parentId).
        // Angular sets `this.booking.parent = this.parent` before submit.
        parent: { id: userId },
        sitter: isAgencyBooking || !sitterIdParam ? undefined : { id: sitterIdParam },
        agencyId: isAgencyBooking ? agencyIdParam : undefined,
        caregiverType: isAgencyBooking ? CAREGIVER_TYPE_AGENCY : undefined,
        typeId,
        numberOfChildren,
        kidsAges: isChildCare ? kidsAges : undefined,
        hasDog: isPetCare ? hasDog : undefined,
        hasCat: isPetCare ? hasCat : undefined,
        hasOtherPet: isPetCare ? hasOtherPet : undefined,
        description,
        includes: selectedIncludes,
        sweeteners: selectedSweeteners,
        rate: Number(rate),
        // API expects paymentFrequency: { id } not a string — matches Angular
        // booking-constants.js paymentFrequencies (hourly=1, flatRate=2).
        paymentFrequency: { id: paymentFrequency === 'flat_rate' ? 2 : 1 },
        paymentType,
        address: {
          street,
          street2: street2 || undefined,
          city,
          state,
          zip,
          neighborhood: neighborhood || undefined,
        },
        phone: !hasStoredPhone ? phone : undefined,
        type: 'job',
      };

      // Recurring: create one booking per instance. Angular POSTs the full
      // {booking, instances} payload to /v2/recurring-bookings; the React
      // us-web-services library does not expose that endpoint so we loop.
      // TODO: verify — if BE later exposes a typed recurring-bookings service,
      // switch to the single-call form to match Angular semantics exactly.
      let lastResp: Booking | null = null;
      if (isRecurring) {
        const responses = await Promise.all(
          instances.map((inst) =>
            BookingService.create({
              ...basePayload,
              start: toApiDateTime(inst.start),
              end: toApiDateTime(inst.end),
            } as never),
          ),
        );
        lastResp = (responses[responses.length - 1]?.data?.data ?? null) as Booking | null;
      } else {
        const resp = await BookingService.create({
          ...basePayload,
          start: start ? toApiDateTime(start) : undefined,
          end: end ? toApiDateTime(end) : undefined,
        } as never);
        lastResp = (resp.data?.data ?? null) as Booking | null;
      }

      cookies.remove(Storage.BOOKING_ORIGIN, { path: '/' });
      // Mirror Angular add-details.js:290,296 — Dashboard reads these cookies
      // to surface the agency/recurring success modals if the user navigates
      // away from the inline success UI.
      if (isAgencyBooking) {
        cookies.set(Storage.AGENCY_BOOKING_SUCCESS, 'true', { path: '/' });
      } else if (isRecurring) {
        cookies.set(Storage.RECURRING_SUCCESS, sitter?.firstName ?? '', { path: '/' });
      }
      tracker.track('booking-created', {
        sitterId: sitterIdParam,
        typeId,
        numberOfChildren,
      });
      // Angular add-details.js:286 — separate event for first-booking completion.
      if (isFirstBooking) {
        tracker.track('booking-details', {
          action: 'first-booking-created',
          label: 'Send Request Button',
        });
      }
      setLastBooking(lastResp);

      // Post-booking promo flow — Angular add-details.js:311 (processCheckForJobs).
      // Only shows for non-agency, non-recurring, child-care bookings when the
      // parent has zero open jobs.
      if (!isAgencyBooking && !isRecurring && isChildCare && lastResp) {
        try {
          const openJobsResp = await JobService.getByFilter({
            params: { parentId: userId, status: 'open', limit: 1 },
          });
          const openJobs = (openJobsResp.data?.data ?? []) as Array<{ id?: number }>;
          if (openJobs.length === 0) {
            // Angular sets ONE_TWO_PUNCH cookie with JSON-stringified booking
            // summary (add-details.js:330). A separate component reads it on
            // the next page and opens the modal; we do it inline here to
            // avoid cross-page coupling.
            cookies.set(
              Storage.ONE_TWO_PUNCH,
              JSON.stringify({
                id: lastResp.id,
                sitter: { firstName: sitter?.firstName },
              }),
              { path: '/' },
            );
            setPromoOpen(true);
            return; // wait for user's choice in the promo modal
          }
        } catch {
          // Non-fatal — fall through to normal success UX.
        }
      }
      setSuccessOpen(true);
    } catch (err: unknown) {
      // Angular add-details.js:235 — first-booking creation error analytic.
      if (isFirstBooking) {
        tracker.track('booking-details', {
          action: 'first booking creation error',
          label: 'Send Request Button',
        });
      }
      const anyErr = err as { response?: { data?: { errors?: Array<{ code?: string; message?: string }> } } };
      const firstErr = anyErr?.response?.data?.errors?.[0];
      const code = firstErr?.code ?? '';
      if (code === 'agency.not_available' || code === 'agency.location_unavailable') {
        setAgencyErrorOpen(true);
      } else if (code.indexOf('booking.payment-switch') > -1) {
        // Angular add-details.js:251 — payment-switch = payment method only.
        setPaywallPaymentOnly(true);
        setPaywallOpen(true);
      } else if (code.indexOf('booking.payment') > -1 || code.indexOf('booking.plan') > -1) {
        // Angular add-details.js:258-267 — open paywall for plan selection.
        setPaywallPaymentOnly(false);
        setPaywallOpen(true);
      } else {
        setError(firstErr?.message ?? "Couldn't send booking. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const dependentOptions = isHouseholdServices ? [] : [1, 2, 3, 4, 5, 6];

  const recipientName = isAgencyBooking
    ? (agency?.firstName ?? agency?.name ?? 'the agency')
    : (sitter?.firstName ?? 'the caregiver');
  const showPhotoUploader =
    !parent?.pictureUrl || parent.pictureUrl.includes('us-default-avatar');

  // Mirrors parent-booking-date-summary.tpl.html — used as the header column
  // on /parent/bookings/add-details. Shows the booked caregiver, the date
  // range, and a link to change the date or view the caregiver's calendar.
  const formatLongDateTime = (iso?: string | null): string =>
    formatDateRange(iso ?? undefined);
  const dateRange = ((): string => {
    if (!isRecurring || instances.length === 0) return '';
    const sorted = [...instances].sort((a, b) =>
      new Date(a.start).getTime() - new Date(b.start).getTime(),
    );
    return `Between ${getMonthDayString(sorted[0].start)} - ${getMonthDayString(sorted[sorted.length - 1].end)}`;
  })();
  const changeDate = (): void => {
    const sp = new URLSearchParams(searchParams);
    if (isRecurring) {
      navigate(`/parent/bookings/frequency?${sp.toString()}`);
      return;
    }
    if (start) sp.set('start', start);
    if (end) sp.set('end', end);
    sp.set('changingDates', 'true');
    if (isAgencyBooking) {
      navigate(`/parent/bookings/agency/date?${sp.toString()}`);
    } else {
      navigate(`/parent/bookings/select-date?${sp.toString()}`);
    }
  };

  return (
    <div className='container py-8 max-w-6xl mx-auto'>
      {showMinRateWarning && sitter && (
        <div className='us-card bg-amber-50 border-l-4 border-amber-400 p-3 mb-4'>
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className='text-amber-600 mr-2'
          />
          {sitter.firstName}&apos;s minimum rate is ${sitterMinimumRate}/hr. They
          may decline jobs below this rate.
        </div>
      )}

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {/* Date summary — Angular: parent-booking-date-summary */}
        <div className='us-card rounded p-4'>
          <div className='flex items-start gap-3 mb-3'>
            {!isAgencyBooking && sitter?.pictureUrl && (
              <img
                src={sitter.pictureUrl}
                alt={sitter.firstName ?? ''}
                className='w-16 h-20 rounded object-cover'
              />
            )}
            {isAgencyBooking && agency?.pictureUrl && (
              <img
                src={agency.pictureUrl}
                alt={agency.firstName ?? agency.name ?? ''}
                className='w-16 h-20 rounded object-cover'
              />
            )}
            <h1 className='text-2xl font-semibold leading-tight'>
              {isAgencyBooking
                ? 'View details and book'
                : `Review details and book ${sitter?.firstName ?? ''}`}
            </h1>
          </div>

          <h3 className='font-semibold mb-1'>
            Date{isRecurring && instances.length > 1 ? 's' : ''} and Time:
          </h3>
          {isRecurring && instances.length > 0 ? (
            <div className='text-sm space-y-1'>
              <div>{instances.length} dates: {dateRange}</div>
              {instances.map((inst, i) => (
                <div key={i}>{formatLongDateTime(inst.start)} – {formatLongDateTime(inst.end)}</div>
              ))}
            </div>
          ) : (
            <div className='text-sm space-y-1'>
              <div>Starts {formatLongDateTime(start)}</div>
              <div>Ends {formatLongDateTime(end)}</div>
            </div>
          )}

          <Button
            id='change-date'
            buttonType={ButtonType.tertiary}
            onClick={changeDate}
            className='block mt-3 underline text-sapphire'
          >
            <i className='fa-regular fa-pen-to-square mr-1' aria-hidden='true' />
            {t('pages.parentBookingWizard.addDetails.changeDateOrTime')}
          </Button>

          {!isAgencyBooking && sitter?.id && (
            <a
              id='view-calendar'
              href={`/app/sitter/${sitter.id}/calendar`}
              target='_blank'
              rel='noreferrer'
              className='block mt-2 underline text-sapphire'
            >
              <i className='fa-regular fa-calendar mr-1' aria-hidden='true' />
              {t('pages.parentBookingWizard.addDetails.viewAvailability', { name: sitter.firstName ?? '' })}
            </a>
          )}

          {showPhotoUploader && (
            <div className='mt-4 pt-4 border-t border-neutral-200'>
              <ProfilePhotoUploader
                parentId={userId}
                pictureUrl={parent?.pictureUrl}
                onUploaded={(): void => {
                  void stores.parent.refreshParent?.();
                }}
              />
            </div>
          )}
        </div>

        {/* Describe the Job */}
        <div className='us-card rounded p-4'>
          <h2 className='font-semibold mb-3'>{t('pages.parentBookingWizard.addDetails.describeJob')}</h2>
          <div className='flex justify-between items-baseline mb-1'>
            <h3 className='font-semibold'>{t('pages.parentBookingWizard.addDetails.jobDescription')}</h3>
            <span className='text-sm text-neutral-600'>
              {t('pages.parentBookingWizard.addDetails.charactersRemaining', { count: DESC_MAX - description.length })}
            </span>
          </div>
          <textarea
            id='message-bubble-text'
            className='form-control w-full border border-neutral-300 rounded p-2 mb-1'
            rows={5}
            maxLength={DESC_MAX}
            placeholder={t('pages.parentBookingWizard.addDetails.descriptionPlaceholder')}
            value={description}
            onChange={(e): void => setDescription(e.target.value)}
          />
          {isHouseholdServices && !description && (
            <p className='text-red-600 text-sm mb-2'>
              {t('pages.parentBookingWizard.addDetails.descriptionRequired')}
            </p>
          )}
          <p className='text-sm text-neutral-600 mb-3'>
            {t('pages.parentBookingWizard.addDetails.noContact')}
          </p>

          {isChildCare && includes.length > 0 && (
            <>
              <h3 className='font-semibold mt-4 mb-2'>{t('pages.parentBookingWizard.addDetails.jobIncludes')}</h3>
              <div className='space-y-1'>
                {includes.map((item, idx) => (
                  <label key={item.type ?? idx} className='flex items-center gap-2'>
                    <input
                      id={item.type}
                      type='checkbox'
                      checked={!!item.selected}
                      onChange={(e): void =>
                        setIncludes((prev) =>
                          prev.map((p, i) =>
                            i === idx ? { ...p, selected: e.target.checked } : p,
                          ),
                        )
                      }
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          {isChildCare && sweeteners.length > 0 && (
            <>
              <h3 className='font-semibold mt-4 mb-2'>Sweeten the deal</h3>
              <div className='space-y-1'>
                {sweeteners.map((item, idx) => (
                  <label key={item.type ?? idx} className='flex items-center gap-2'>
                    <input
                      id={item.type}
                      type='checkbox'
                      checked={!!item.selected}
                      onChange={(e): void =>
                        setSweeteners((prev) =>
                          prev.map((p, i) =>
                            i === idx ? { ...p, selected: e.target.checked } : p,
                          ),
                        )
                      }
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Job Details */}
        <div className='us-card rounded p-4'>
          <h2 className='font-semibold mb-3'>Job Details</h2>

          {!isHouseholdServices && (
            <div className='mb-3'>
              <label htmlFor='num-dependents' className='block font-semibold mb-1'>
                {dependentLabel}:
              </label>
              <select
                id='num-dependents'
                className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                value={numberOfChildren}
                onChange={(e): void => setNumberOfChildren(Number(e.target.value))}
              >
                {dependentOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isChildCare && (
            <div className='mb-3'>
              <label htmlFor='kids-ages' className='block font-semibold mb-1'>
                Age{numberOfChildren === 1 ? '' : 's'}
              </label>
              <input
                id='kids-ages'
                type='text'
                className={`form-control w-full border rounded px-3 py-2 ${
                  !kidsAges ? 'border-red-500' : 'border-neutral-300'
                }`}
                value={kidsAges}
                onChange={(e): void => setKidsAges(e.target.value)}
              />
              {!kidsAges && (
                <p className='text-red-600 text-sm mt-1'>
                  Tell us how old the{' '}
                  {numberOfChildren === 1 ? 'child is' : 'children are'}.
                </p>
              )}
            </div>
          )}

          {isPetCare && (
            <div className='mb-3 text-center'>
              <p className='text-sm mb-2'>
                {t('pages.parentBookingWizard.addDetails.petTypePrompt', { count: numberOfChildren })}
              </p>
              <div className='flex justify-center gap-3'>
                <Button
                  buttonType={ButtonType.secondary}
                  onClick={(): void => setHasDog(!hasDog)}
                  className={`px-3 py-2 rounded border ${hasDog ? 'border-green-500 bg-green-50' : 'border-neutral-300'}`}
                >
                  <FontAwesomeIcon icon={faDog} className='mr-2' />
                  {t('pages.parentBookingWizard.addDetails.dog')}
                </Button>
                <Button
                  buttonType={ButtonType.secondary}
                  onClick={(): void => setHasCat(!hasCat)}
                  className={`px-3 py-2 rounded border ${hasCat ? 'border-green-500 bg-green-50' : 'border-neutral-300'}`}
                >
                  <FontAwesomeIcon icon={faCat} className='mr-2' />
                  {t('pages.parentBookingWizard.addDetails.cat')}
                </Button>
                <Button
                  buttonType={ButtonType.secondary}
                  onClick={(): void => setHasOtherPet(!hasOtherPet)}
                  className={`px-3 py-2 rounded border ${hasOtherPet ? 'border-green-500 bg-green-50' : 'border-neutral-300'}`}
                >
                  <FontAwesomeIcon icon={faPaw} className='mr-2' />
                  {t('pages.parentBookingWizard.addDetails.otherPet')}
                </Button>
              </div>
            </div>
          )}

          <h3 className='font-semibold mb-2'>Payment type</h3>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <div className='col-span-4'>
              <div className='flex items-center border border-neutral-300 rounded'>
                <span className='px-2 bg-neutral-100'>$</span>
                <input
                  id='rate'
                  type='tel'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  className={`flex-1 px-2 py-2 ${!isRateValid ? 'text-red-600' : ''}`}
                  value={rate}
                  onChange={(e): void => setRate(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>
            <div className='col-span-8'>
              {canSelectFlatRate ? (
                <select
                  id='paymentFrequencyType'
                  aria-label={t('pages.parentBookingWizard.addDetails.rateAriaLabel')}
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  value={paymentFrequency}
                  onChange={(e): void => setPaymentFrequency(e.target.value as 'hourly' | 'flat_rate')}
                >
                  <option value='hourly'>{t('pages.parentBookingWizard.addDetails.perHourOption')}</option>
                  <option value='flat_rate'>{t('pages.parentBookingWizard.addDetails.flatRateOption')}</option>
                </select>
              ) : (
                <div className='form-control w-full border border-neutral-300 rounded px-3 py-2 bg-neutral-50'>
                  {t('pages.parentBookingWizard.addDetails.hourly')}
                </div>
              )}
            </div>
          </div>

          <fieldset className='mt-3'>
            <legend className='font-semibold mb-1'>{t('pages.parentBookingWizard.addDetails.paymentMethod')}</legend>
            {isAgencyBooking || sitter?.acceptsCreditCards ? (
              <div className='grid grid-cols-2 gap-2'>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='payment-types'
                    id='type-credit'
                    value='credit'
                    checked={paymentType === 'credit'}
                    onChange={(): void => setPaymentType('credit')}
                  />
                  {t('pages.parentBookingWizard.addDetails.creditCard')}
                </label>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='payment-types'
                    id='type-cash'
                    value='cash'
                    checked={paymentType === 'cash'}
                    onChange={(): void => setPaymentType('cash')}
                  />
                  {t('pages.parentBookingWizard.addDetails.cash')}
                </label>
              </div>
            ) : (
              <input
                aria-label={t('pages.parentBookingWizard.addDetails.creditCardUnavailable')}
                className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                value={t('pages.parentBookingWizard.addDetails.cashOnly')}
                disabled
              />
            )}
          </fieldset>

          <h3 className='font-semibold mt-4 mb-2'>{t('pages.parentBookingWizard.addDetails.locationOfJob')}</h3>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <input
              id='address-form-street'
              type='text'
              className={`col-span-8 form-control border rounded px-3 py-2 ${
                !street ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder={t('pages.parentBookingWizard.addDetails.street')}
              value={street}
              onChange={(e): void => setStreet(e.target.value)}
            />
            <input
              id='address-form-street2'
              type='text'
              className='col-span-4 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentBookingWizard.addDetails.apt')}
              value={street2}
              onChange={(e): void => setStreet2(e.target.value)}
            />
          </div>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <input
              id='address-form-city'
              type='text'
              className={`col-span-8 form-control border rounded px-3 py-2 ${
                !city ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder={t('pages.parentBookingWizard.addDetails.city')}
              value={city}
              onChange={(e): void => setCity(e.target.value)}
            />
            <select
              id='address-form-state'
              className={`col-span-4 form-control border rounded px-3 py-2 ${
                !state ? 'border-red-500' : 'border-neutral-300'
              }`}
              value={state}
              onChange={(e): void => setState(e.target.value)}
            >
              <option value=''>{t('pages.parentBookingWizard.addDetails.state')}</option>
              {STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <input
              id='address-form-neighborhood'
              type='text'
              className='col-span-8 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentBookingWizard.addDetails.neighborhood')}
              value={neighborhood}
              onChange={(e): void => setNeighborhood(e.target.value)}
            />
            <input
              id='address-form-zip'
              type='tel'
              maxLength={5}
              className={`col-span-4 form-control border rounded px-3 py-2 ${
                !zip || zip.length !== 5 ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder={t('pages.parentBookingWizard.addDetails.zipCode')}
              value={zip}
              onChange={(e): void => setZip(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          {!isAddressComplete && (
            <p className='text-red-600 text-sm'>
              {t('pages.parentBookingWizard.addDetails.addressError')}
            </p>
          )}

          {!hasStoredPhone && (
            <div className='mt-3'>
              <label htmlFor='booking-phone' className='block font-semibold mb-1'>
                {t('pages.parentBookingWizard.addDetails.phone')}
              </label>
              <input
                id='booking-phone'
                type='tel'
                maxLength={12}
                placeholder={t('pages.parentBookingWizard.addDetails.phonePlaceholder')}
                className={`form-control w-full border rounded px-3 py-2 ${
                  !isPhoneValid ? 'border-red-500' : 'border-neutral-300'
                }`}
                value={phone}
                onChange={(e): void => setPhone(e.target.value)}
              />
              {!isPhoneValid && phone && (
                <p className='text-red-600 text-sm mt-1'>
                  {t('pages.parentBookingWizard.addDetails.phoneError')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {error && <p className='text-red-600 text-sm mt-4'>{error}</p>}

      <div className='flex gap-3 justify-center mt-6'>
        <Button
          id='btn-book'
          buttonType={ButtonType.primary}
          onClick={submit}
          disabled={!canSubmit}
        >
          {t('pages.parentBookingWizard.addDetails.book')}
        </Button>
        <Button
          buttonType={ButtonType.secondary}
          onClick={(): void => navigate(-1)}
        >
          {t('common.back')}
        </Button>
      </div>

      <BookingSuccessModal
        isOpen={successOpen}
        onClose={(): void => {
          setSuccessOpen(false);
          navigate('/bookings');
        }}
        recipientName={recipientName}
      />
      <AgencyErrorModal
        isOpen={agencyErrorOpen}
        onClose={(): void => setAgencyErrorOpen(false)}
        onSearch={(): void => {
          setAgencyErrorOpen(false);
          navigate('/sitters');
        }}
      />

      {/*
        Paywall modal. Angular add-details.js:258-267 — when BE returns
        booking.plan or booking.payment, show the paywall, and retry the save
        after the user selects a plan / adds a card.
      */}
      {userId && (
        <FamilyPaywallModal
          showModal={paywallOpen}
          paymentOnly={paywallPaymentOnly}
          header={
            paywallPaymentOnly
              ? 'To complete this booking, add a payment method'
              : 'To complete this booking, please select a plan'
          }
          sitter={sitter as never}
          userId={userId}
          stripeKey={config.STRIPE_KEY}
          recaptchaKey={config.RECAPTCHA_KEY}
          onClose={(): void => setPaywallOpen(false)}
          onSuccess={(): void => {
            setPaywallOpen(false);
            void submit();
          }}
        />
      )}

      {/* Post-booking "one-two punch" promo modal. Shown when the booking
          succeeded, the parent has zero open jobs, and the booking was child
          care. */}
      <Modal
        isOpen={promoOpen}
        onClose={(): void => {
          setPromoOpen(false);
          cookies.remove(Storage.ONE_TWO_PUNCH, { path: '/' });
          navigate('/bookings');
        }}
        title={t('pages.parentBookingWizard.promo.title')}
        className='max-w-md'
        footer={
          <>
            <Button
              buttonType={ButtonType.tertiary}
              onClick={(): void => {
                setPromoOpen(false);
                cookies.remove(Storage.ONE_TWO_PUNCH, { path: '/' });
                navigate('/bookings');
              }}
            >
              {t('pages.parentBookingWizard.promo.noThanks')}
            </Button>
            <Button
              id='post-booking-job-post'
              buttonType={ButtonType.primary}
              onClick={(): void => {
                setPromoOpen(false);
                cookies.remove(Storage.ONE_TWO_PUNCH, { path: '/' });
                const presetId = lastBooking?.id;
                navigate(presetId ? `/job-post?presetId=${presetId}` : '/job-post');
              }}
            >
              {t('pages.parentBookingWizard.promo.postJob')}
            </Button>
          </>
        }
      >
        <p>{t('pages.parentBookingWizard.promo.body')}</p>
      </Modal>
    </div>
  );
});

export const ParentBookingWizard = observer(({ step }: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const [searchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  useEffect(() => {
    document.title = 'UrbanSitter - Booking';
  }, []);

  if (step === 'frequency') {
    return <FrequencyStep searchParams={searchParams} userId={user?.id} />;
  }
  if (step === 'selection') {
    return <SelectionStep searchParams={searchParams} userId={user?.id} />;
  }
  if (step === 'selectDate' || step === 'agencyDate') {
    return (
      <SelectDateStep
        searchParams={searchParams}
        userId={user?.id}
        isAgency={step === 'agencyDate'}
      />
    );
  }
  if (step === 'confirmMultipleDates') {
    return <ConfirmMultipleDatesStep searchParams={searchParams} />;
  }
  if (step === 'addDetails') {
    return <AddDetailsStep searchParams={searchParams} userId={user?.id} />;
  }

  return <div className='container py-8'>{t('pages.parentBookingWizard.deferred')}</div>;
});
